import posthog from "posthog-js";

const { REACT_APP_POSTHOG_API_KEY, REACT_APP_POSTHOG_HOST } = process.env;

const UTM_COOKIE = "ph_utm";
const CONSENT_COOKIE = "ph_consent";
const SESSION_COOKIE = "ph_session_id";
const CONSENTED_SERVICE_NAME = "PostHog.com";

const cookieDomainAttr = () => {
  const parts = window.location.hostname.split(".");
  return parts.length > 2 ? `; domain=.${parts.slice(-2).join(".")}` : "";
};

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name, value, maxAgeSeconds) => {
  const maxAge = maxAgeSeconds ? `; max-age=${maxAgeSeconds}` : "";
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; path=/${cookieDomainAttr()}${maxAge}; SameSite=Lax`;
};

const captureUtm = () => {
  if (getCookie(UTM_COOKIE)) return;

  const params = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach(
    (key) => {
      const value = params.get(key);
      if (value) utm[key] = value;
    }
  );

  if (Object.keys(utm).length > 0) {
    setCookie(UTM_COOKIE, JSON.stringify(utm), 7776000);
  }
};

const applyInitialUtmProperties = () => {
  const utmCookie = getCookie(UTM_COOKIE);
  if (!utmCookie) return;

  try {
    const utm = JSON.parse(utmCookie);
    const setOnce = {};
    if (utm.utm_source) setOnce.$initial_utm_source = utm.utm_source;
    if (utm.utm_medium) setOnce.$initial_utm_medium = utm.utm_medium;
    if (utm.utm_campaign) setOnce.$initial_utm_campaign = utm.utm_campaign;
    if (utm.utm_term) setOnce.$initial_utm_term = utm.utm_term;
    if (utm.utm_content) setOnce.$initial_utm_content = utm.utm_content;

    if (Object.keys(setOnce).length > 0) {
      posthog.capture("$set", { $set_once: setOnce });
    }
  } catch (e) {
    // malformed cookie, ignore
  }
};

export const optIn = () => {
  posthog.opt_in_capturing();
  posthog.reloadFeatureFlags();
  setCookie(CONSENT_COOKIE, "1");
  applyInitialUtmProperties();
};

export const optOut = () => {
  posthog.opt_out_capturing();
  setCookie(CONSENT_COOKIE, "0");
};

// Call once a real user identity (e.g. UUID) becomes available later in the flow.
export const identifyUser = (uuid) => {
  if (uuid) posthog.identify(String(uuid));
};

const applyUsercentricsConsent = () => {
  if (!window.UC_UI) return;

  Promise.resolve(window.UC_UI.getServicesBaseInfo()).then((services) => {
    const list = Array.isArray(services) ? services : Object.values(services || {});
    const service = list.find((s) => s.name === CONSENTED_SERVICE_NAME);
    const consented =
      service && (service.consent === true || (service.consent && service.consent.status));

    if (consented) {
      optIn();
    } else {
      optOut();
    }
  });
};

export const initPostHog = () => {
  if (!REACT_APP_POSTHOG_API_KEY || !REACT_APP_POSTHOG_HOST) return false;

  captureUtm();

  posthog.init(REACT_APP_POSTHOG_API_KEY, {
    api_host: REACT_APP_POSTHOG_HOST,
    person_profiles: "always",
    persistence: "localStorage+cookie",
    cross_subdomain_cookie: true,
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    advanced_disable_decide: true,
    loaded: () => {
      applyUsercentricsConsent();
    },
  });

  posthog.onSessionId((sessionId) => {
    setCookie(SESSION_COOKIE, sessionId);
  });

  window.addEventListener("UC_UI_INITIALIZED", applyUsercentricsConsent);
  window.addEventListener("UC_UI_CMP_EVENT", applyUsercentricsConsent);

  return true;
};

export default posthog;
